import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { WellbeingFactor } from '../types/patient';

interface WellbeingMeterProps {
  categoryName: string;
  categoryScore: number;
  factors: {
    name: string;
    data: WellbeingFactor;
  }[];
}

/**
 * Gets the color class based on the score (1-10 scale)
 * Red: 0-4 (needs attention)
 * Yellow: 5-8 (moderate)
 * Green: 9-10 (healthy)
 */
const getColorFromScore = (score: number): string => {
  if (score <= 4) return 'bg-red-500';
  if (score <= 8) return 'bg-yellow-500';
  return 'bg-green-500';
};

/**
 * Gets text color class for score display
 */
const getTextColorFromScore = (score: number): string => {
  if (score <= 4) return 'text-red-700';
  if (score <= 8) return 'text-yellow-700';
  return 'text-green-700';
};

/**
 * Gets background color class for the category card
 */
const getBgColorFromScore = (score: number): string => {
  if (score <= 4) return 'bg-red-50 border-red-200';
  if (score <= 8) return 'bg-yellow-50 border-yellow-200';
  return 'bg-green-50 border-green-200';
};

export const WellbeingMeter: React.FC<WellbeingMeterProps> = ({
  categoryName,
  categoryScore,
  factors,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const percentage = (categoryScore / 10) * 100;
  const colorClass = getColorFromScore(categoryScore);
  const textColorClass = getTextColorFromScore(categoryScore);
  const bgColorClass = getBgColorFromScore(categoryScore);

  return (
    <div className={`border rounded-lg p-4 transition-all ${bgColorClass}`}>
      {/* Category Header with Meter */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full text-left"
      >
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-gray-800">{categoryName}</h3>
          <div className="flex items-center gap-3">
            <span className={`text-2xl font-bold ${textColorClass}`}>
              {categoryScore.toFixed(1)}/10
            </span>
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-600" />
            )}
          </div>
        </div>

        {/* Horizontal Meter Bar */}
        <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
          <div
            className={`h-full ${colorClass} transition-all duration-500 ease-out flex items-center justify-end pr-2`}
            style={{ width: `${percentage}%` }}
          >
            {percentage > 15 && (
              <span className="text-white text-xs font-semibold">
                {Math.round(percentage)}%
              </span>
            )}
          </div>
        </div>
      </button>

      {/* Expandable Details */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-gray-300 space-y-4">
          {factors.map((factor, index) => (
            <div key={index} className="bg-white rounded-lg p-3 shadow-sm">
              <div className="flex justify-between items-start mb-2">
                <span className="font-medium text-gray-800">{factor.name}</span>
                <span
                  className={`font-semibold ${getTextColorFromScore(factor.data.score)}`}
                >
                  {factor.data.score}/10
                </span>
              </div>

              {/* Individual Factor Meter */}
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2 overflow-hidden">
                <div
                  className={`h-full ${getColorFromScore(factor.data.score)} transition-all duration-300`}
                  style={{ width: `${(factor.data.score / 10) * 100}%` }}
                />
              </div>

              {/* Patient Comment */}
              {factor.data.comment && (
                <p className="text-sm text-gray-600 italic mt-2">
                  "{factor.data.comment}"
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
